import React, { useState, useEffect } from "react";
import {
  Bell,
  CheckCircle2,
  AlertCircle,
  Clock,
  ExternalLink,
  X,
  Loader2,
  CheckCheck,
  Inbox,
} from "lucide-react";
import {
  listInternalNotifications,
  markInternalNotificationRead,
} from "@/lib/recruitmentApi";
import { getApiErrorMessage, logAppError } from "@/lib/api";

export default function NotificationsDrawer({
  isOpen,
  onClose,
  onSelectApplication,
  onSelectNotification,
}) {
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all"); // "all" | "unread"

  const loadNotifications = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await listInternalNotifications(null, 50);
      setNotifications(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(getApiErrorMessage(err, "Erreur de récupération des notifications"));
      logAppError("NotificationsDrawer.loadNotifications", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadNotifications();
    }
  }, [isOpen]);

  const handleMarkAsRead = async (notificationId) => {
    try {
      await markInternalNotificationRead(notificationId);
      setNotifications((prev) =>
        prev.map((n) =>
          n.notification_id === notificationId ? { ...n, read: true, read_at: new Date().toISOString() } : n
        )
      );
    } catch (err) {
      logAppError("NotificationsDrawer.markAsRead", err, { notificationId });
    }
  };

  const filteredList = notifications.filter((n) => {
    if (filter === "unread") return !n.read;
    return true;
  });

  const unreadCount = notifications.filter((n) => !n.read).length;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-black/40 backdrop-blur-xs flex justify-end">
      <div className="bg-white w-full max-w-md h-full shadow-2xl flex flex-col border-l border-slate-200">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-900 text-white">
          <div className="flex items-center gap-2.5">
            <Bell className="w-5 h-5 text-indigo-400" />
            <div>
              <h2 className="text-sm font-semibold">Notifications Recrutement</h2>
              <p className="text-[11px] text-slate-300">
                {unreadCount > 0 ? `${unreadCount} non lue(s)` : "Toutes les alertes sont lues"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Filters */}
        <div className="px-5 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between text-xs">
          <div className="flex gap-2">
            <button
              onClick={() => setFilter("all")}
              className={`px-2.5 py-1 rounded-md font-medium transition-colors ${
                filter === "all"
                  ? "bg-white text-indigo-700 shadow-xs border border-slate-200"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Toutes ({notifications.length})
            </button>
            <button
              onClick={() => setFilter("unread")}
              className={`px-2.5 py-1 rounded-md font-medium transition-colors ${
                filter === "unread"
                  ? "bg-white text-indigo-700 shadow-xs border border-slate-200"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Non lues ({unreadCount})
            </button>
          </div>
          <button
            onClick={loadNotifications}
            className="text-[11px] text-slate-500 hover:text-indigo-600"
          >
            Actualiser
          </button>
        </div>

        {/* Content list */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2.5">
          {isLoading ? (
            <div className="py-12 flex flex-col items-center justify-center text-slate-400">
              <Loader2 className="w-6 h-6 animate-spin mb-2" />
              <p className="text-xs">Chargement des notifications...</p>
            </div>
          ) : filteredList.length === 0 ? (
            <div className="py-12 text-center text-slate-400 space-y-2">
              <Inbox className="w-8 h-8 mx-auto text-slate-300" />
              <p className="text-xs">Aucune notification à afficher.</p>
            </div>
          ) : (
            filteredList.map((notif) => (
              <div
                key={notif.notification_id}
                className={`p-3.5 rounded-xl border text-xs transition-colors ${
                  notif.read
                    ? "bg-white border-slate-200 opacity-80"
                    : "bg-indigo-50/50 border-indigo-200 shadow-xs"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="space-y-1 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span
                        className={`text-[9px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wider ${
                          notif.priority === "high"
                            ? "bg-rose-100 text-rose-800"
                            : notif.priority === "low"
                            ? "bg-slate-100 text-slate-600"
                            : "bg-indigo-100 text-indigo-800"
                        }`}
                      >
                        {notif.priority}
                      </span>
                      <span className="font-semibold text-slate-900">{notif.title}</span>
                    </div>
                    <p className="text-slate-600 leading-relaxed">{notif.message}</p>
                    <div className="flex items-center gap-2 pt-1 text-[11px] text-slate-400">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(notif.created_at).toLocaleTimeString("fr-FR", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                      {notif.metadata?.resource_id && (
                        <span>ID: {notif.metadata.resource_id.substring(0, 8)}...</span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-1 items-end">
                    {!notif.read && (
                      <button
                        onClick={() => handleMarkAsRead(notif.notification_id)}
                        className="p-1 text-slate-400 hover:text-emerald-600 hover:bg-white rounded transition-colors"
                        title="Marquer comme lu"
                      >
                        <CheckCheck className="w-4 h-4" />
                      </button>
                    )}
                    {(notif.metadata?.application_id || notif.metadata?.need_id || notif.metadata?.candidate_id) && (
                      <button
                        onClick={() => {
                          if (onSelectNotification) {
                            onSelectNotification(notif);
                          } else if (onSelectApplication) {
                            onSelectApplication(notif.metadata?.application_id);
                          }
                          onClose();
                        }}
                        className="p-1 text-slate-400 hover:text-indigo-600 hover:bg-white rounded transition-colors"
                        title="Ouvrir le dossier concerné"
                        data-testid="notification-open-dossier"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
